import { useEffect, useRef } from 'react';
import { z } from 'zod/v3';
import { Card, CardContent } from '@/components/shadcn/card';
import { Checkbox } from '@/components/shadcn/checkbox';
import { Field, FieldGroup, FieldLabel, FieldSet } from '@/components/shadcn/field';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/radio-group';
import {
  govtIntakeFormLegacyDataLiveToday,
  govtIntakeFormLegacyDataQuality,
  govtIntakeFormReporting,
  govtIntakeModuleUploadExistingForms,
  govtIntakeModuleUploadExistingReports,
} from '@/data/govtIntakeStaticData.ts';
import type { ValidationChange } from '@/modules/governmentIntake/base-entity.tsx';
import HeaderAndSub from '@/modules/governmentIntake/components/shared-component';
import SharedHeader from '@/modules/governmentIntake/components/shared-component';
import { type FileData, type ProgramSubSections, useIntakeForm } from '@/modules/governmentIntake/context/intake-form-context.tsx';
import { createFieldChange, useFormController } from '../../../hooks/useFormController';
import UploadBox from '../../UploadBox';

const fileDataSchema = z.object({
  name: z.string(),
  size: z.number(),
  type: z.string(),
  data: z.string(), // Base64 encoded
});

const scopingSchema = z
  .object({
    anticipatedPrograms: z.string(),
    automatedReporting: z.string(),
    reportCount: z.string(),
    automatedReportingFile: z.array(fileDataSchema),

    programForms: z.string(),
    formCount: z.string(),
    automatedFormFile: z.array(fileDataSchema),

    consolidate: z.string(),
    dataLocations: z.array(z.string()),

    numberOfProperties: z.string(),
    approxParticipants: z.string(),
    approxDocumentsFiles: z.string(),
    dataQuality: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.automatedReporting === 'Yes') {
      if (!data.reportCount) {
        ctx.addIssue({
          path: ['reportCount'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }

      if (!data.automatedReportingFile?.length) {
        ctx.addIssue({
          path: ['automatedReportingFile'],
          message: 'Upload required',
          code: z.ZodIssueCode.custom,
        });
      }
    }

    if (data.programForms === 'Yes') {
      if (!data.formCount) {
        ctx.addIssue({
          path: ['formCount'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }

      if (!data.automatedFormFile?.length) {
        ctx.addIssue({
          path: ['automatedFormFile'],
          message: 'Upload required',
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });
export type ProgramSubScopingData = z.infer<typeof scopingSchema>;

const EMPTY_FORM: ProgramSubScopingData = {
  anticipatedPrograms: '',
  automatedReporting: '',
  reportCount: '',
  automatedReportingFile: [] as FileData[],

  programForms: '',
  formCount: '',
  automatedFormFile: [] as FileData[],

  consolidate: '',
  dataLocations: [] as string[],

  numberOfProperties: '',
  approxParticipants: '',
  approxDocumentsFiles: '',
  dataQuality: '',
};

export default function ProgramSubScoping({ onValidationChange }: ValidationChange) {
  const { data, updateProgramSubSection } = useIntakeForm();

  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  const programsItem = data.primaryObjectOfWork?.find((item): item is { programs: ProgramSubSections } => 'programs' in item);

  const form = useFormController<ProgramSubScopingData>({
    schema: scopingSchema,
    initialData: programsItem?.programs?.scoping ?? EMPTY_FORM,
    onChange: (v) => updateProgramSubSection('scoping', v),
  });

  useEffect(() => {
    onValidationChangeRef.current?.(form.isValid);
  }, [form.isValid]);

  const onChange = createFieldChange<ProgramSubScopingData>(form);

  const handleAutomatedReportingChange = (value: ProgramSubScopingData['automatedReporting']) => {
    form.setField('automatedReporting', value);
    if (value === 'No') {
      form.setField('reportCount', '0' as ProgramSubScopingData['reportCount']);
    }
  };

  const handleProgramFormsChange = (value: ProgramSubScopingData['programForms']) => {
    form.setField('programForms', value);
    if (value === 'No') {
      form.setField('formCount', '0' as ProgramSubScopingData['formCount']);
    }
  };

  const handleToggleDataLocation = (label: string, checked: boolean | 'indeterminate') => {
    const isChecked = checked === true;
    const currentLocations = form.data.dataLocations || [];
    const newLocations = isChecked ? [...currentLocations, label] : currentLocations.filter((loc) => loc !== label);
    form.setField('dataLocations', newLocations);
  };

  return (
    <ProgramSubScopingUI
      formData={form.data}
      onChange={onChange}
      onAutomatedReportingChange={handleAutomatedReportingChange}
      onProgramFormsChange={handleProgramFormsChange}
      onSetAutomatedReportingFiles={(files) => form.setField('automatedReportingFile', files)}
      onSetAutomatedFormFiles={(files) => form.setField('automatedFormFile', files)}
      onSetConsolidate={(value) => form.setField('consolidate', value)}
      onToggleDataLocation={handleToggleDataLocation}
    />
  );
}

type ProgramSubScopingUIProps = {
  formData: ProgramSubScopingData;
  onChange: ReturnType<typeof createFieldChange<ProgramSubScopingData>>;
  onAutomatedReportingChange: (value: ProgramSubScopingData['automatedReporting']) => void;
  onProgramFormsChange: (value: ProgramSubScopingData['programForms']) => void;
  onSetAutomatedReportingFiles: (files: FileData[]) => void;
  onSetAutomatedFormFiles: (files: FileData[]) => void;
  onSetConsolidate: (value: string) => void;
  onToggleDataLocation: (label: string, checked: boolean | 'indeterminate') => void;
};

function ProgramSubScopingUI({
  formData,
  onChange,
  onAutomatedReportingChange,
  onProgramFormsChange,
  onSetAutomatedReportingFiles,
  onSetAutomatedFormFiles,
  onSetConsolidate,
  onToggleDataLocation,
}: ProgramSubScopingUIProps) {
  const needsReports = formData.automatedReporting === 'Yes';
  const needsForms = formData.programForms === 'Yes';
  const needsConsolidate = formData.consolidate === 'Yes' || formData.consolidate === 'Not Sure';

  return (
    <div className='flex w-full flex-col overflow-y-auto'>
      <div className='w-full max-w-5xl'>
        <header className='pt-4'>
          <SharedHeader title='Scoping' subTitle='Define what you need from SEIN Terminal and what data will be migrated.' />
        </header>
        <section className='flex-1 overflow-y-auto pb-4'>
          <div className='grid grid-cols-1 lg:grid-cols-2 py-8 gap-6'>
            {/*Terminal*/}
            <Card className='w-full shadow-none border-gray-300'>
              <CardContent>
                <HeaderAndSub title='What you need from SEIN Terminal' subTitle='Platform requirements and outputs' titleClass='text-lg' />
                <div className='space-y-3 pt-8'>
                  <div className='space-y-2'>
                    <Label className='block text-sm font-semibold text-gray-900 mb-2'>How many programs do you anticipate managing?</Label>
                    <Input
                      className='text-sm'
                      type='number'
                      placeholder='Enter anticipated number of programs'
                      value={formData.anticipatedPrograms}
                      onChange={onChange('anticipatedPrograms')}
                    />
                  </div>
                  <Label className='font-semibold pt-4'>Do you need automated reporting?</Label>
                  <RadioGroup
                    value={formData.automatedReporting}
                    onValueChange={(v) => onAutomatedReportingChange(v as ProgramSubScopingData['automatedReporting'])}
                  >
                    {govtIntakeFormReporting.map((item) => (
                      <div key={item.id} className='flex items-center gap-2'>
                        <RadioGroupItem value={item.title} id={item.id} />
                        <Label className='cursor-pointer select-none' htmlFor={item.id}>
                          {item.title}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>

                  {needsReports && (
                    <Card className='mx-6 p-4 shadow-none'>
                      <div className='space-y-2'>
                        {/*<Label>How many reports do you need?</Label>*/}
                        <Input
                          className='text-sm'
                          type='number'
                          placeholder='Enter number of reports'
                          value={formData.reportCount}
                          onChange={onChange('reportCount')}
                        />
                      </div>
                      {formData.reportCount && (
                        <div className='text-sm text-gray-500'>
                          <Label>{govtIntakeModuleUploadExistingReports}</Label>
                          <UploadBox files={formData.automatedReportingFile ?? []} onChange={onSetAutomatedReportingFiles} />
                        </div>
                      )}
                    </Card>
                  )}
                </div>

                <div className='space-y-3 pt-8'>
                  <Label className='font-semibold'>
                    Do you need us to digitize any public or internally facing forms and/or applications?
                  </Label>
                  <RadioGroup
                    value={formData.programForms}
                    onValueChange={(v) => onProgramFormsChange(v as ProgramSubScopingData['programForms'])}
                  >
                    {govtIntakeFormReporting
                      .filter((item) => item.id !== 'notSure')
                      .map((item) => (
                        <div key={item.id} className='flex items-center gap-2'>
                          <RadioGroupItem value={item.title} id={`forms-${item.id}`} />
                          <Label className='cursor-pointer select-none' htmlFor={`forms-${item.id}`}>
                            {item.title}
                          </Label>
                        </div>
                      ))}
                  </RadioGroup>

                  {needsForms && (
                    <Card className='p-4 mx-6 shadow-none'>
                      <div className='space-y-2'>
                        {/*<Label>Do you need forms?</Label>*/}
                        <Input
                          type='number'
                          className='text-sm'
                          placeholder='Enter number of forms'
                          value={formData.formCount}
                          onChange={onChange('formCount')}
                        />
                      </div>
                      {formData.formCount && (
                        <div className='text-sm text-gray-500'>
                          <Label>{govtIntakeModuleUploadExistingForms}</Label>
                          <UploadBox files={formData.automatedFormFile ?? []} onChange={onSetAutomatedFormFiles} />
                        </div>
                      )}
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>

            {/*Consolidation*/}
            <Card className='w-full shadow-none border-gray-300'>
              <CardContent className='px-6'>
                <HeaderAndSub
                  title='What SEIN Terminal is inheriting'
                  subTitle='Existing data and migration requirements'
                  titleClass='text-lg'
                />
                <div className='pt-8'>
                  <HeaderAndSub
                    title='Do you need SEIN Terminal to consolidate existing data into a platform?'
                    titleClass='text-base'
                    subTitle='Consolidate means importing program lists and related records/documents so reporting and tracking can be done in one place.'
                    subtitleClass='text-gray-400'
                  />
                  <div className='space-y-3'>
                    <RadioGroup value={formData.consolidate} onValueChange={(v) => onSetConsolidate(v)}>
                      {govtIntakeFormReporting.map((item) => (
                        <div key={item.id} className='flex items-center gap-2'>
                          <RadioGroupItem value={item.title} id={`consolidate-${item.id}`} />
                          <Label className='cursor-pointer select-none' htmlFor={`consolidate-${item.id}`}>
                            {item.title}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                </div>

                {/* Data location */}
                <div className='space-y-2 pt-8'>
                  <Label className='block text-base tracking-tight font-semibold text-gray-700 mb-4'>Where does data live today?</Label>
                  {govtIntakeFormLegacyDataLiveToday.map((item) => {
                    const id = `data-live-${item.id}`;
                    const isChecked = formData.dataLocations?.includes(item.label) ?? false;

                    return (
                      <FieldSet key={id}>
                        <FieldGroup>
                          <Field orientation='horizontal'>
                            <Checkbox
                              id={id}
                              checked={isChecked}
                              onCheckedChange={(checked) => onToggleDataLocation(item.label, checked)}
                            />
                            <FieldLabel htmlFor={id} className='cursor-pointer select-none'>
                              {item.label}
                            </FieldLabel>
                          </Field>
                        </FieldGroup>
                      </FieldSet>
                    );
                  })}
                </div>

                {/*Rough estimates and data quality only if consolidate = yes*/}
                {needsConsolidate && (
                  <div className='space-y-8 mt-8'>
                    <div className='space-y-8'>
                      <Label className='block text-base font-semibold text-gray-900 mb-4'>Rough volume estimates</Label>
                      <div>
                        <Label className='block text-sm font-medium text-gray-900 mb-1'>Number of programs</Label>
                        <Input
                          type='text'
                          value={formData.numberOfProperties}
                          onChange={onChange('numberOfProperties')}
                          placeholder='e.g., 5-10'
                          className='w-full px-3 py-2 rounded-lg border-2 border-gray-300 focus:border-3 focus:outline-none focus:border-green-600'
                        />
                      </div>

                      <div>
                        <Label className='block text-sm font-medium text-gray-900 mb-1'>Approx participants</Label>
                        <Input
                          type='text'
                          value={formData.approxParticipants}
                          onChange={onChange('approxParticipants')}
                          placeholder='e.g., 500-1000'
                          className='w-full px-3 py-2 rounded-lg border-2 border-gray-300 focus:border-3 focus:outline-none focus:border-green-600'
                        />
                      </div>

                      <div>
                        <Label className='block text-sm font-medium text-gray-900 mb-1'>Approx documents/files</Label>
                        <Input
                          type='text'
                          value={formData.approxDocumentsFiles}
                          onChange={onChange('approxDocumentsFiles')}
                          placeholder='e.g., 100-500'
                          className='w-full px-3 py-2 rounded-lg border-2 border-gray-300 focus:border-3 focus:outline-none focus:border-green-600'
                        />
                      </div>
                    </div>
                    <div>
                      <Label className='block text-sm font-semibold text-gray-900 mb-3'>Data quality</Label>
                      <div className='space-y-2'>
                        {govtIntakeFormLegacyDataQuality.map((option) => (
                          <Label key={option.id} className='flex items-center gap-2 cursor-pointer'>
                            <Input
                              type='radio'
                              value={option.id}
                              checked={formData.dataQuality === option.id}
                              onChange={onChange('dataQuality')}
                              className='w-4 h-4 accent-green-600 cursor-pointer'
                            />
                            <span className='text-sm text-gray-700'>{option.label}</span>
                          </Label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
}
